import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Upload, Loader2, AlertCircle, CheckCircle, X, FileText } from 'lucide-react';
import { documentApi, uploadApi } from '../services/api';
import { generateDocumentR2Key, formatFileSize, isValidPDF } from '@pdfsign/shared/utils';

const createDocumentSchema = z.object({
  title: z.string().min(1, 'Tiêu đề là bắt buộc').max(200),
  description: z.preprocess(
    (val) => (val === '' || val === null ? undefined : val),
    z.string().max(1000).optional()
  ),
  type: z.enum(['bao_cao', 'quyet_dinh', 'to_trinh', 'cong_van', 'hop_dong', 'khac']),
  workflow_type: z.enum(['sequential', 'parallel']).default('sequential'),
  expires_in_days: z.preprocess(
    (val) => (val === '' || val === null || val === undefined || (typeof val === 'number' && Number.isNaN(val)) ? undefined : Number(val)),
    z.number().int('Số ngày phải là số nguyên').min(1, 'Tối thiểu 1 ngày').max(365, 'Tối đa 365 ngày').optional()
  ),
  signing_date: z.preprocess(
    (val) => (val === '' || val === null ? undefined : val),
    z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Định dạng ngày không hợp lệ').optional()
  ),
});

type CreateDocumentForm = z.infer<typeof createDocumentSchema>;

const typeLabels: Record<string, string> = {
  bao_cao: 'Báo cáo',
  quyet_dinh: 'Quyết định',
  to_trinh: 'Tờ trình',
  cong_van: 'Công văn',
  hop_dong: 'Hợp đồng',
  khac: 'Khác',
};

export default function DocumentCreatePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'info' | 'upload' | 'complete'>('info');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [pagesCount, setPagesCount] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [uploadKey, setUploadKey] = useState<string>('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CreateDocumentForm>({
    resolver: zodResolver(createDocumentSchema),
    defaultValues: {
      workflow_type: 'sequential',
    },
  });

  const onSubmit = async (data: CreateDocumentForm) => {
    setError(null);
    try {
      const payload: any = {
        title: data.title,
        description: data.description,
        type: data.type,
        workflow_type: data.workflow_type,
        expires_in_days: data.expires_in_days,
      };
      if (data.signing_date) {
        payload.settings = {
          signing_date: data.signing_date,
        };
      }
      const response = await documentApi.create(payload);
      const newDocumentId = response.data.data.document.id;
      setDocumentId(newDocumentId);
      setUploadKey(response.data.data.upload?.key || '');
      setStep('upload');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể tạo tài liệu');
    }
  };

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!isValidPDF(selectedFile)) {
      setError('Chỉ chấp nhận file PDF');
      return;
    }

    const maxSize = 50 * 1024 * 1024; // 50MB
    if (selectedFile.size > maxSize) {
      setError('File không được vượt quá 50MB');
      return;
    }

    setFile(selectedFile);
    setError(null);

    // Detect page count with pdf.js (dynamic import keeps the initial bundle small)
    try {
      const pdfjs = await import('pdfjs-dist');
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url
      ).toString();
      const buffer = await selectedFile.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: buffer }).promise;
      setPagesCount(pdf.numPages);
    } catch {
      setPagesCount(1);
    }
  }, []);

  const calculateFileHash = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const handleUpload = async () => {
    if (!file || !documentId) return;

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Calculate hash
      const hash = await calculateFileHash(file);

      // Upload to R2 using the key issued by the API
      const r2Key = uploadKey;

      await uploadApi.directUpload(r2Key, file, setUploadProgress);

      // Complete upload
      await documentApi.completeUpload(documentId, {
        file_name: file.name,
        file_size: file.size,
        file_hash: hash,
        pages_count: pagesCount,
        r2_key: r2Key,
      });

      setStep('complete');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Lỗi khi tải lên file');
    } finally {
      setUploading(false);
    }
  };

  if (step === 'complete') {
    return (
      <div className="page-container max-w-2xl">
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-success-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Tài liệu đã được tạo thành công!</h1>
          <p className="text-gray-600 mb-8">File PDF đã được tải lên và sẵn sàng để gửi ký.</p>
          
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => navigate(`/documents/${documentId}`)}
              className="btn-primary"
            >
              Xem tài liệu
            </button>
            <button
              onClick={() => navigate('/documents')}
              className="btn-outline"
            >
              Về danh sách
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="pb-2 border-b border-slate-200/80 dark:border-white/10">
        <h1 className="page-title">Tạo tài liệu mới</h1>
        <p className="page-subtitle">Tải lên file PDF và thiết lập quy trình phân luồng người ký</p>
      </div>

      {/* Progress steps */}
      <div className="mb-6">
        <div className="flex items-center">
          <StepIndicator step={1} current={step === 'info' ? 1 : step === 'upload' ? 2 : 3} label="Thông tin" />
          <div className="flex-1 h-1 bg-slate-200 dark:bg-slate-800 mx-4" />
          <StepIndicator step={2} current={step === 'info' ? 1 : step === 'upload' ? 2 : 3} label="Tải file PDF" />
          <div className="flex-1 h-1 bg-slate-200 dark:bg-slate-800 mx-4" />
          <StepIndicator step={3} current={step === 'info' ? 1 : step === 'upload' ? 2 : 3} label="Hoàn tất" />
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-3 text-danger-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-danger-600 hover:text-danger-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {step === 'info' && (
        <form onSubmit={handleSubmit(onSubmit)} className="card p-6 space-y-6">
          <div>
            <label htmlFor="title" className="label">Tiêu đề tài liệu <span className="text-danger-500">*</span></label>
            <input
              {...register('title')}
              id="title"
              type="text"
              className={`input ${errors.title ? 'input-error' : ''}`}
              placeholder="Ví dụ: Báo cáo tài chính Q4 2024"
            />
            {errors.title && <p className="mt-1 text-sm text-danger-600">{errors.title.message}</p>}
          </div>

          <div>
            <label htmlFor="description" className="label">Mô tả</label>
            <textarea
              {...register('description')}
              id="description"
              rows={3}
              className="input"
              placeholder="Mô tả ngắn gọn về nội dung tài liệu..."
            />
          </div>

          <div>
            <label htmlFor="type" className="label">Loại tài liệu <span className="text-danger-500">*</span></label>
            <select
              {...register('type')}
              id="type"
              className={`input ${errors.type ? 'input-error' : ''}`}
            >
              <option value="">Chọn loại tài liệu</option>
              {Object.entries(typeLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            {errors.type && <p className="mt-1 text-sm text-danger-600">{errors.type.message}</p>}
          </div>

          <div>
            <label className="label">Quy trình ký <span className="text-danger-500">*</span></label>
            <div className="grid grid-cols-2 gap-4">
              <label className="relative cursor-pointer">
                <input
                  type="radio"
                  {...register('workflow_type')}
                  value="sequential"
                  className="sr-only"
                />
                <input
                  type="radio"
                  {...register('workflow_type')}
                  value="sequential"
                  className="sr-only"
                />
                <div className={`p-4 rounded-xl border text-center transition-all ${
                  watch('workflow_type') === 'sequential'
                    ? 'border-sky-500 bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 shadow-sm'
                    : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 text-slate-700 dark:text-slate-300'
                }`}>
                  <div className="text-sm font-medium">Tuần tự</div>
                  <div className="text-xs text-slate-400 mt-1 font-normal">Ký lần lượt theo thứ tự danh sách</div>
                </div>
              </label>
              <label className="relative cursor-pointer">
                <input
                  type="radio"
                  {...register('workflow_type')}
                  value="parallel"
                  className="sr-only"
                />
                <div className={`p-4 rounded-xl border text-center transition-all ${
                  watch('workflow_type') === 'parallel'
                    ? 'border-sky-500 bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 shadow-sm'
                    : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 text-slate-700 dark:text-slate-300'
                }`}>
                  <div className="text-sm font-medium">Song song</div>
                  <div className="text-xs text-slate-400 mt-1 font-normal">Tất cả người ký cùng lúc độc lập</div>
                </div>
              </label>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="expires_in_days" className="label text-xs mb-0">Hết hạn sau (ngày)</label>
              <span className="text-[11px] text-slate-400">Tùy chọn: Thời hạn hiệu lực của link ký</span>
            </div>
            <input
              {...register('expires_in_days')}
              id="expires_in_days"
              type="number"
              min="1"
              max="365"
              className={`input text-xs ${errors.expires_in_days ? 'input-error' : ''}`}
              placeholder="Để trống = Không giới hạn thời gian ký"
            />
            {errors.expires_in_days && (
              <p className="mt-1 text-xs text-rose-500">{errors.expires_in_days.message}</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="signing_date" className="label text-xs mb-0">
                Ngày ấn định ký (Tùy chọn - Ký hồi cứu)
              </label>
              <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">Văn bản lùi ngày</span>
            </div>
            <input
              {...register('signing_date')}
              id="signing_date"
              type="date"
              className={`input text-xs ${errors.signing_date ? 'input-error' : ''}`}
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Khóa ngày/tháng/năm hiển thị dưới chữ ký theo ngày này (giờ/phút vẫn tự động ghi nhận theo thời điểm ký thực tế). Để trống = lấy ngày giờ thực tế.
            </p>
            {errors.signing_date && (
              <p className="mt-1 text-xs text-rose-500">{errors.signing_date.message}</p>
            )}
          </div>

          <div className="flex gap-3 pt-3">
            <button type="submit" className="btn-primary text-xs flex-1 h-9 font-medium">
              Tiếp theo: Tải file
            </button>
          </div>
        </form>
      )}

      {step === 'upload' && (
        <div className="card p-6 space-y-6">
          <div className="text-center">
            <FileText className="w-12 h-12 text-sky-500 mx-auto mb-3" />
            <h2 className="text-base font-medium text-slate-900 dark:text-white mb-1">Tải lên tệp PDF</h2>
            <p className="text-xs text-slate-400 font-normal">Kéo thả tệp PDF vào khu vực dưới đây hoặc nhấn để chọn file</p>
          </div>

          <div className="border border-dashed border-slate-300 dark:border-white/15 rounded-xl p-8 text-center hover:border-sky-500/50 transition-colors relative bg-slate-50/50 dark:bg-white/[0.02]">
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploading}
            />
            <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            {file ? (
              <div className="text-left max-w-md mx-auto">
                <div className="bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-white/10 rounded-xl p-3.5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-7 h-7 text-rose-500" />
                      <div>
                        <p className="text-xs font-medium text-slate-900 dark:text-white">{file.name}</p>
                        <p className="text-[11px] text-slate-400 font-mono">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setFile(null)}
                      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Kéo thả tệp PDF vào đây</p>
                <p className="text-xs text-slate-400 mt-1 font-normal">hoặc nhấn để chọn tệp từ máy tính</p>
                <p className="text-[11px] text-slate-500 mt-2 font-mono">Định dạng .pdf · Tối đa 50MB</p>
              </>
            )}
          </div>

          {uploading && (
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-xs mb-1 text-slate-400">
                  <span>Đang tải lên...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-600 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {file && !uploading && (
            <div className="flex gap-4 justify-center">
              <button
                onClick={handleUpload}
                className="btn-primary flex-1 max-w-xs"
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Đang tải...
                  </>
                ) : (
                  'Xác nhận và tải lên'
                )}
              </button>
              <button
                onClick={() => setFile(null)}
                className="btn-outline flex-1 max-w-xs"
              >
                Chọn file khác
              </button>
            </div>
          )}

          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              Lưu ý: File PDF sẽ được lưu trữ an toàn trên Cloudflare R2 với mã hóa AES-256.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function StepIndicator({ step, current, label }: { step: number; current: number; label: string }) {
  const isActive = current >= step;
  const isCurrent = current === step;
  
  return (
    <div className="flex flex-col items-center">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold transition-all ${
        isActive
          ? 'bg-sky-500 text-slate-950 shadow-md shadow-sky-500/20'
          : 'bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-transparent dark:border-white/5'
      } ${isCurrent ? 'ring-4 ring-sky-500/20' : ''}`}>
        {isActive && step !== current ? <CheckCircle className="w-4 h-4" /> : step}
      </div>
      <span className={`mt-2 text-xs font-semibold ${isActive ? 'text-sky-600 dark:text-sky-400' : 'text-slate-400 dark:text-slate-500'}`}>
        {label}
      </span>
    </div>
  );
}